import { useState } from "react";

import { Collapse, Icon } from "@blueprintjs/core";
import uniqueId from "lodash/uniqueId";

import { Box } from "@/components/layout/flexbox";
import { FlexibleButton } from "@/components/pieces/button/FlexibleButton";
import { CARD_CLASSES } from "@/css/constants";
import { SurveyTemplatesQuery } from "@/graphql";
import { cn } from "@/lib/utils";
import { ConfigureInput, NextInputProps } from "@/types/programs";
import { ensureNonNullArray } from "@/utils/array";
import { NonNullableArrayItem } from "@/utils/types";

type SurveyTemplate = NonNullableArrayItem<
  SurveyTemplatesQuery["surveyTemplates"]
>;

interface TemplateProps {
  onNext: (data: NextInputProps) => void;
  input: ConfigureInput;
  surveyTemplateData?: SurveyTemplatesQuery;
}

export function Template({ onNext, input, surveyTemplateData }: TemplateProps) {
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  const handleStartFromScratch = () => {
    onNext({
      value: {
        formFields: [],
        name: "",
        participantGuidance: "",
      },
      isSave: false,
    });
  };

  const handleTemplateSelection = (template: SurveyTemplate) => {
    const oldQuestions = ensureNonNullArray(input?.formFields);
    const newFields = ensureNonNullArray(template.formFields);
    const newQuestions = newFields
      .filter(
        (item) =>
          !oldQuestions.some((question) => question.prompt === item.prompt),
      )
      .map((field) => ({
        id: uniqueId("new-by-template"),
        prompt: field.prompt,
        order: field.order,
        answerType: field.answerType,
        options: ensureNonNullArray(field.formFieldOptions).map((option) => ({
          id: option.id,
          value: option.value,
          label: option.label,
        })),
      }));

    onNext({
      value: {
        name: input?.name || template.name,
        participantGuidance:
          input?.participantGuidance || template.participantGuidance,
        formFields: [...oldQuestions, ...newQuestions],
      },
      isSave: false,
    });
  };

  const toggleTemplateQuestions = (templateId: string) => {
    setSelectedTemplateIds((ids) =>
      ids.includes(templateId)
        ? ids.filter((id) => id !== templateId)
        : [...ids, templateId],
    );
  };

  const templates = ensureNonNullArray(surveyTemplateData?.surveyTemplates);

  return (
    <div className="flex flex-col gap-6 max-w-148 my-0 mx-auto p-9">
      <div>
        <div className="text-2xl font-semibold">
          Want to start from a template?
        </div>
        <div className="text-subtext mt-2.5">
          Get a head start by using one of our pre-built surveys. You can
          customize in the next step.
        </div>
      </div>
      <div>
        <FlexibleButton
          rightIcon="chevron-right"
          onClick={handleStartFromScratch}
        >
          Start From Scratch
        </FlexibleButton>
      </div>

      {templates.length > 0 && (
        <div className="flex flex-col gap-4">
          {templates.map((template) => {
            const isOpen = selectedTemplateIds.includes(template.id);
            const rightIcon = isOpen ? "chevron-up" : "chevron-down";

            return (
              <div
                key={template.id}
                className={cn(CARD_CLASSES, "flex-1 p-4 flex flex-col gap-2")}
              >
                <div className="font-semibold">{template.name}</div>
                <div className="text-subtext">{template.description}</div>
                {template.formFields && (
                  <div>
                    <a onClick={() => toggleTemplateQuestions(template.id)}>
                      {isOpen ? "Hide questions" : "See questions"}
                      <Icon
                        icon={rightIcon}
                        style={{ marginLeft: 5, fontWeight: 500 }}
                      />
                    </a>
                    <Collapse isOpen={isOpen} transitionDuration={100}>
                      {isOpen && (
                        <Box mt={3}>
                          <ul
                            className="list-disc pl-6"
                            style={{ margin: 0, padding: "0px 20px" }}
                          >
                            {ensureNonNullArray(template.formFields).map(
                              (question) => (
                                <li key={question.id} style={{ marginTop: 10 }}>
                                  {question.prompt}
                                </li>
                              ),
                            )}
                          </ul>
                        </Box>
                      )}
                    </Collapse>
                  </div>
                )}

                <div className="mt-2">
                  <FlexibleButton
                    onClick={() => handleTemplateSelection(template)}
                  >
                    Use Template
                  </FlexibleButton>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
